/**
 * CalmHeroSection Component
 * Mountain landscape hero with floating stats cards for AdElevate
 * Supports dynamic color themes: blue, purple, green
 */

import React, { useState, useEffect } from 'react';

// Theme color configurations
const themeColors = {
  blue: {
    primary: 'from-blue-500 to-sky-500',
    primaryHover: 'from-blue-600 to-sky-600',
    gradient: 'from-blue-400 via-sky-500 to-blue-400',
    bgGlow1: 'rgba(59,130,246,0.15)',
    bgGlow2: 'rgba(14,165,233,0.15)',
    starColor: 'text-blue-400',
    badgeBg: 'bg-blue-500/90',
    ctaGlow: 'shadow-blue-500/30 hover:shadow-blue-500/50',
    logoBg: 'from-blue-500 to-sky-500',
    statCardBg: 'bg-blue-500/20',
    statCardBorder: 'border-blue-400/30',
  },
  purple: {
    primary: 'from-purple-500 to-pink-500',
    primaryHover: 'from-purple-600 to-pink-600',
    gradient: 'from-purple-400 via-pink-500 to-purple-400',
    bgGlow1: 'rgba(168,85,247,0.15)',
    bgGlow2: 'rgba(236,72,153,0.15)',
    starColor: 'text-purple-400',
    badgeBg: 'bg-purple-500/90',
    ctaGlow: 'shadow-purple-500/30 hover:shadow-purple-500/50',
    logoBg: 'from-purple-500 to-pink-500',
    statCardBg: 'bg-purple-500/20',
    statCardBorder: 'border-purple-400/30',
  },
  green: {
    primary: 'from-green-500 to-emerald-500',
    primaryHover: 'from-green-600 to-emerald-600',
    gradient: 'from-green-400 via-emerald-500 to-green-400',
    bgGlow1: 'rgba(34,197,94,0.15)',
    bgGlow2: 'rgba(16,185,129,0.15)',
    starColor: 'text-green-400',
    badgeBg: 'bg-green-500/90',
    ctaGlow: 'shadow-green-500/30 hover:shadow-green-500/50',
    logoBg: 'from-green-500 to-emerald-500',
    statCardBg: 'bg-green-500/20',
    statCardBorder: 'border-green-400/30',
  }
};

// Floating Stats Card Component
const FloatingStatCard = ({ children, className = '', delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div 
      className={`backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl shadow-2xl transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      } ${className}`}
      style={{ animation: isVisible ? 'floatCard 6s ease-in-out infinite' : 'none' }}
    >
      {children}
    </div>
  );
};

const CalmHeroSection = ({ 
  colorTheme = 'blue', 
  onGetStarted, 
  showcaseVideoUrl = null,
  showNav = false
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const colors = themeColors[colorTheme] || themeColors.blue;
  
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen overflow-hidden bg-slate-900" data-testid="calm-hero-section">
      {/* Background - Mountain Landscape */}
      <div className="absolute inset-0">
        {/* Gradient overlay for text readability */}
        <div 
          className="absolute inset-0 z-10"
          style={{
            background: `linear-gradient(
              135deg, 
              rgba(15, 23, 42, 0.7) 0%, 
              rgba(15, 23, 42, 0.3) 30%,
              rgba(139, 92, 246, 0.1) 50%,
              rgba(236, 72, 153, 0.1) 70%,
              rgba(15, 23, 42, 0.5) 100%
            )`
          }}
        />
        
        {/* Mountain background image - bright daylight version (served from S3 for fast loading) */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://adelevate.s3.ap-southeast-2.amazonaws.com/assets/landing/hero-mountain-landscape.png')`,
            filter: 'brightness(1.0) saturate(1.1)'
          }}
        />
        
        {/* Animated gradient orbs */}
        <div className="absolute inset-0 z-5 overflow-hidden pointer-events-none">
          <div 
            className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full opacity-20"
            style={{
              background: `radial-gradient(circle, ${colors.bgGlow1} 0%, transparent 70%)`,
              animation: 'calmFloat 20s ease-in-out infinite'
            }}
          />
          <div 
            className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full opacity-15"
            style={{
              background: `radial-gradient(circle, ${colors.bgGlow2} 0%, transparent 70%)`,
              animation: 'calmFloat 15s ease-in-out infinite reverse'
            }}
          />
        </div>
      </div>

      {/* Floating Stats Cards */}
      <div className="absolute inset-0 z-15 pointer-events-none hidden lg:block">
        {/* Top Left - ROAS Card */}
        <FloatingStatCard 
          className="absolute top-32 left-8 xl:left-16 p-4 min-w-[180px]"
          delay={600}
        >
          <div className="flex items-center gap-3">
            <div className="flex flex-col">
              <div className="flex items-baseline gap-1">
                <span style={{ color: '#22C55E' }} className="text-2xl font-bold">+320%</span>
              </div>
              <span style={{ color: '#E2E8F0' }} className="text-sm">ROAS</span>
            </div>
            <div className="w-px h-10 bg-white/20"></div>
            <div className="flex flex-col">
              <span style={{ color: '#FFFFFF' }} className="text-2xl font-bold">1.2M</span>
              <span style={{ color: '#E2E8F0' }} className="text-sm">Impressions</span>
            </div>
          </div>
        </FloatingStatCard>

        {/* Top Right - Campaigns Card */}
        <FloatingStatCard 
          className="absolute top-40 right-8 xl:right-16 p-4"
          delay={800}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-white text-2xl font-bold">12</span>
              <span className="text-white/70 text-sm">Campaigns Published</span>
            </div>
          </div>
        </FloatingStatCard>

        {/* Floating Icons */}
        <div className="absolute top-1/3 right-1/4 animate-bounce" style={{ animationDelay: '0.5s', animationDuration: '3s' }}>
          <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
            </svg>
          </div>
        </div>
        
        <div className="absolute bottom-1/3 right-16 animate-bounce" style={{ animationDelay: '1s', animationDuration: '4s' }}>
          <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
            <svg className="w-5 h-5 text-pink-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </div>
        </div>
      </div>

      {/* Hero Content - Responsive positioning */}
      <div className="relative z-20 flex flex-col items-center justify-center min-h-screen px-4 md:px-6 text-center pt-24 md:pt-16 pb-12 md:mt-[-40px]">
        {/* Main Headline - Clean and Calm */}
        <div 
          className={`transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h1 
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-light mb-2 leading-[1.15] tracking-tight"
            style={{ 
              fontFamily: 'Georgia, "Times New Roman", serif',
              color: '#F8FAFC',
              textShadow: '0 2px 20px rgba(0,0,0,0.5), 0 4px 40px rgba(0,0,0,0.3)'
            }}
          >
            Marketing, reimagined
          </h1>
          <h2 
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-light mb-6 md:mb-8 leading-[1.15] tracking-tight"
            style={{ 
              fontFamily: 'Georgia, "Times New Roman", serif',
              background: 'linear-gradient(135deg, #1D4ED8 0%, #2563EB 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              filter: 'drop-shadow(0 2px 10px rgba(37, 99, 235, 0.4))'
            }}
          >
            peacefully.
          </h2>
        </div>

        {/* Subheadline - Dark text for readability */}
        <p 
          className={`text-base md:text-lg lg:text-xl max-w-xl mb-8 md:mb-10 leading-relaxed transition-all duration-1000 delay-200 px-2 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{
            color: '#1F2937',
            textShadow: '0 1px 3px rgba(255,255,255,0.6)'
          }}
        >
          Let AI handle the chaos. Create stunning ads in 60 seconds while you focus on what matters most.
        </p>

        {/* CTA Button */}
        <div 
          className={`transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <button 
            onClick={onGetStarted}
            className="group relative px-8 md:px-10 py-3 md:py-4 text-white text-base md:text-lg font-semibold rounded-full shadow-2xl hover:scale-105 transition-all duration-300"
            data-testid="calm-hero-cta"
            style={{
              backgroundColor: '#2563EB',
              boxShadow: '0 4px 30px rgba(37, 99, 235, 0.4), 0 8px 40px rgba(0,0,0,0.2)'
            }}
          >
            <span className="relative z-10">Start Creating for Free</span>
          </button>
          
          <p className="mt-4 text-sm font-medium" style={{ color: '#E5E7EB', textShadow: '0 2px 4px rgba(0,0,0,0.4)' }}>
            No credit card required · 100 free credits
          </p>
        </div>

        {/* Quick Stats Row - Mobile visible */}
        <div 
          className={`mt-6 md:mt-10 flex flex-wrap justify-center gap-3 md:gap-6 lg:hidden transition-all duration-1000 delay-600 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="backdrop-blur-xl bg-white/80 border border-white/60 rounded-xl px-3 md:px-4 py-2 md:py-3 shadow-lg">
            <span style={{ color: '#22C55E' }} className="font-bold text-lg md:text-xl">+320%</span>
            <span style={{ color: '#6B7280' }} className="text-xs md:text-sm ml-1 md:ml-2">ROAS</span>
          </div>
          <div className="backdrop-blur-xl bg-white/80 border border-white/60 rounded-xl px-3 md:px-4 py-2 md:py-3 shadow-lg">
            <span style={{ color: '#1F2937' }} className="font-bold text-lg md:text-xl">1.2M</span>
            <span style={{ color: '#6B7280' }} className="text-xs md:text-sm ml-1 md:ml-2">Impressions</span>
          </div>
          <div className="backdrop-blur-xl bg-white/80 border border-white/60 rounded-xl px-3 md:px-4 py-2 md:py-3 shadow-lg">
            <span style={{ color: '#2563EB' }} className="font-bold text-lg md:text-xl">12</span>
            <span style={{ color: '#6B7280' }} className="text-xs md:text-sm ml-1 md:ml-2">Campaigns</span>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes calmFloat {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(30px, 30px); }
        }
        
        @keyframes calmShimmer {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes floatCard {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </section>
  );
};

export default CalmHeroSection;
